import Model from "../../../../model/Model";
import Snapshot from "../../../../model/Snapshot"
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from "@mui/material";
import {
  dateToLocalFormat, humanizeString,
  isoToLocalDateString,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../../stores/StoreUtilities";
import AssetConversion from "../../../../model/AssetConversion";
import Asset, {AssetCategory, Risk} from "../../../../model/Asset";
import Liability from "../../../../model/Liability";
import {INetWorthCalc} from "../../../../components/calculator/Calculator";
import {grey} from "@mui/material/colors";
import {getIncomeTypeDef} from "../../../../model/Income";
import User from "../../../../model/User";
import {GrowthModel} from "../../../../model/GrowthStrategy";
import {isNumber} from "../../../../model/ModelUtilities";

const NetWorthReport = ({
  user,
  model,
  liquidAssets,
  assetConversions,
  realAssets,
  personalAssets,
  otherAssets,
  liabilities,
  snapshot,
  networthCalc
} : {
  user: User
  model: Model
  liquidAssets: Asset[]
  assetConversions: AssetConversion[]
  realAssets: Asset[]
  personalAssets: Asset[]
  otherAssets: Asset[]
  liabilities: Liability[]
  snapshot: Snapshot
  networthCalc: INetWorthCalc
}) => {

  const theme = useTheme()

  const styles = {
    th: {
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.1,
    },
    thTitle: {
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.1,
      width: 200
    },
    tb: {
      fontSize: 14,
    },
    total: {
      fontSize: 14,
      fontWeight: 700
    },
    totalPos: {
      fontSize: 14,
      fontWeight: 700,
      color: "green"
    },
    totalNeg: {
      fontSize: 14,
      fontWeight: 700,
      color: "red"
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      maxHeight: 15,
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    }
  }

  const snapshotDate = dateToLocalFormat(snapshot.date)
  const prevYear = snapshot.date.getFullYear()-1

  const getReturnRate = (asset: Asset) => {
    let rate = ""
    if (asset.assetCategory === AssetCategory.LiquidInvestableAssets) {
      if (asset.rateLock) {
        rate = numberToPercentFormat(asset.returnRate, 2)
      } else {
        const growthStrategy = model.growthStrategy
        if (growthStrategy) {
          if (growthStrategy.growthModel === GrowthModel.Simple) {
            if (!growthStrategy.ignoreCustomGrowthRates && isNumber(asset.returnRate)) {
              rate = numberToPercentFormat(asset.returnRate, 2)
            } else {
              rate = numberToPercentFormat(growthStrategy.growthRate, 2)
            }
          } else {
            if (!growthStrategy.ignoreCustomGrowthRates && isNumber(asset.returnRate)) {
              rate = numberToPercentFormat(asset.returnRate, 2)
            } else {
              rate = `${humanizeString(growthStrategy.returnSequence)}`
            }
          }
        } else {
          rate = numberToPercentFormat(Model.defaultGrowthRate)
        }
      }
    } else if (asset.assetCategory === AssetCategory.RealEstateAndProperty) {
      rate = numberToPercentFormat(asset.returnRate, 2)
    }

    return rate
  }

  const renderNetWorth = () => {
    let netWorth = networthCalc.netWorthTotal

    const color = (netWorth < 0) ? "red" : "green"
    return (
      <Typography variant="h4" color={color}>{numberToMoneyFormat(netWorth, 0)}</Typography>
    )
  }

  const getAssetConversionsTotal = () => {
    return assetConversions.reduce((accum: number, item: AssetConversion) => {
      if (item.amount > 0) {
        return (accum + item.amount)
      } else if (item.percent > 0 && item.srcAsset) {
        return (accum + (item.percent * item.srcAsset.balance))
      } else {
        return 0
      }
    }, 0)
  }

  return (
    <Box>
      <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" pr={1} mt={1}>
        <Typography variant="h5" color="primary" gutterBottom>Liquid Assets</Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" width="100%">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.thTitle}>Title</TableCell>
              <TableCell sx={styles.th}>Owner</TableCell>
              <TableCell sx={styles.th}>Account Type</TableCell>
              <TableCell sx={styles.th}>Inherited Year</TableCell>
              <TableCell sx={styles.th}>Risk</TableCell>
              <TableCell sx={styles.th}>Acquisition Date</TableCell>
              <TableCell sx={styles.th}>Return Rate</TableCell>
              <TableCell sx={styles.th} align="center">Withdrawal Order</TableCell>
              <TableCell sx={styles.th} align="right">{prevYear} End Balance</TableCell>
              <TableCell sx={styles.th} align="right">{snapshotDate} Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {liquidAssets.filter((a: Asset) => !a.archived).map((a: Asset) =>
              <TableRow key={`asset-${a.id}`}>
                <TableCell sx={styles.tb}>{a.description}</TableCell>
                <TableCell sx={styles.tb}>{a.owner?.nickname}</TableCell>
                <TableCell sx={styles.tb}>{a.assetTypeDef.label}</TableCell>
                <TableCell sx={styles.tb}>{a.inheritedYear ?? ""}</TableCell>
                <TableCell sx={styles.tb}>{a.risk !== null && a.risk !== undefined ? humanizeString(Risk[a.risk]) : ""}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(a.startDate)}</TableCell>
                <TableCell sx={styles.tb}>{getReturnRate(a)}</TableCell>
                <TableCell sx={styles.tb} align="center">{a.withdrawalOrder ?? ""}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(a.priorEoYBalance, 0)}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(snapshot.getSnapshotBalance(a.id, "Asset"), 0)}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={styles.total}>Total</TableCell>
              <TableCell sx={styles.totalPos} colSpan={9} align="right">{numberToMoneyFormat(networthCalc.liquidAssetsTotal, 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" minHeight={40} pr={1}>
        <Typography variant="h5" color="primary" gutterBottom>Asset Conversions</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" width="100%">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.thTitle}>Title</TableCell>
              <TableCell sx={styles.th}>Year</TableCell>
              <TableCell sx={styles.th}>Source Asset</TableCell>
              <TableCell sx={styles.th}>Destination Asset</TableCell>
              <TableCell sx={styles.th}>Amount</TableCell>
              <TableCell sx={styles.th}>Taxable Income Type</TableCell>
              <TableCell sx={styles.th}>Tax Excludes</TableCell>
              <TableCell sx={styles.th}>Completed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assetConversions.filter((a: AssetConversion) => !a.archived).map((a: AssetConversion) =>
              <TableRow key={`asset-${a.id}`}>
                <TableCell sx={styles.tb}>{a.description}</TableCell>
                <TableCell sx={styles.tb}>{a.year}</TableCell>
                <TableCell sx={styles.tb}>{a.srcAsset?.description}</TableCell>
                <TableCell sx={styles.tb}>{a.dstAsset?.description}</TableCell>
                <TableCell sx={styles.tb}>{a.amount ? numberToMoneyFormat(a.amount, 0) : numberToPercentFormat(a.percent ?? 0, 0)}</TableCell>
                <TableCell sx={styles.tb}>{getIncomeTypeDef(a.incomeType)?.label}</TableCell>
                <TableCell sx={styles.tb}>{numberToMoneyFormat(a.nonTaxableAmount, 0)}</TableCell>
                <TableCell sx={styles.tb}>{isoToLocalDateString(a.completedAt)}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={styles.total}>Total</TableCell>
              <TableCell sx={styles.totalPos} colSpan={9} align="right">{numberToMoneyFormat(getAssetConversionsTotal(), 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" minHeight={40} pr={1}>
        <Typography variant="h5" color="primary" gutterBottom>Real Estate</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" width="100%">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.thTitle}>Title</TableCell>
              <TableCell sx={styles.th}>Owner</TableCell>
              <TableCell sx={styles.th}>Risk</TableCell>
              <TableCell sx={styles.th}>Acquisition Date</TableCell>
              <TableCell sx={styles.th}>Growth Rate %</TableCell>
              <TableCell sx={styles.th} align="right">{snapshotDate} Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {realAssets.filter((a: Asset) => !a.archived).map((a: Asset) =>
              <TableRow key={`asset-${a.id}`}>
                <TableCell sx={styles.tb}>{a.description}{a.netWorthExcluded ? "*" : ""}</TableCell>
                <TableCell sx={styles.tb}>{a.owner?.nickname}</TableCell>
                <TableCell sx={styles.tb}>{a.risk !== null && a.risk !== undefined ? humanizeString(Risk[a.risk]) : ""}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(a.startDate)}</TableCell>
                <TableCell sx={styles.tb}>{numberToPercentFormat(a.returnRate, 2)}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(snapshot.getSnapshotBalance(a.id, "Asset"), 0)}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={styles.total}>Total</TableCell>
              <TableCell sx={styles.totalPos} colSpan={5} align="right">{numberToMoneyFormat(networthCalc.realAssetsTotal, 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" minHeight={40} pr={1}>
        <Typography variant="h5" color="primary" gutterBottom>Personal Property</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" width="100%">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.thTitle}>Title</TableCell>
              <TableCell sx={styles.th}>Owner</TableCell>
              <TableCell sx={styles.th}>Risk</TableCell>
              <TableCell sx={styles.th}>Acquisition Date</TableCell>
              <TableCell sx={styles.th} align="right">{snapshotDate} Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {personalAssets.filter((a: Asset) => !a.archived).map((a: Asset) =>
              <TableRow key={`asset-${a.id}`}>
                <TableCell sx={styles.tb}>{a.description}{a.netWorthExcluded ? "*" : ""}</TableCell>
                <TableCell sx={styles.tb}>{a.owner?.nickname}</TableCell>
                <TableCell sx={styles.tb}>{a.risk !== null && a.risk !== undefined ? humanizeString(Risk[a.risk]) : ""}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(a.startDate)}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(snapshot.getSnapshotBalance(a.id, "Asset"), 0)}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={styles.total}>Total</TableCell>
              <TableCell sx={styles.totalPos} colSpan={4} align="right">{numberToMoneyFormat(networthCalc.personalAssetsTotal, 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" minHeight={40} pr={1}>
        <Typography variant="h5" color="primary" gutterBottom>Other Assets</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" width="100%">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.thTitle}>Title</TableCell>
              <TableCell sx={styles.th}>Owner</TableCell>
              <TableCell sx={styles.th}>Risk</TableCell>
              <TableCell sx={styles.th}>Acquisition Date</TableCell>
              <TableCell sx={styles.th} align="right">{snapshotDate} Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {otherAssets.filter((a: Asset) => !a.archived).map((a: Asset) =>
              <TableRow key={`asset-${a.id}`}>
                <TableCell sx={styles.tb}>{a.description}{a.netWorthExcluded ? "*" : ""}</TableCell>
                <TableCell sx={styles.tb}>{a.owner?.nickname}</TableCell>
                <TableCell sx={styles.tb}>{a.risk !== null && a.risk !== undefined ? humanizeString(Risk[a.risk]) : ""}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(a.startDate)}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(snapshot.getSnapshotBalance(a.id, "Asset"), 0)}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={styles.total}>Total</TableCell>
              <TableCell sx={styles.totalPos} colSpan={4} align="right">{numberToMoneyFormat(networthCalc.otherAssetsTotal, 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" flexGrow={1} justifyContent="space-between" alignItems="flex-end" minHeight={40} pr={1}>
        <Typography variant="h5" color="primary" gutterBottom>Liabilities</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" width="100%">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.thTitle}>Title</TableCell>
              <TableCell sx={styles.th}>Owner</TableCell>
              <TableCell sx={styles.th}>Interest Rate</TableCell>
              <TableCell sx={styles.th}>Start Date</TableCell>
              <TableCell sx={styles.th}>Payoff Date</TableCell>
              <TableCell sx={styles.th} align="right">{snapshotDate} Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {liabilities.filter((l: Liability) => !l.archived).map((l: Liability) =>
              <TableRow key={`liability-${l.id}`}>
                <TableCell sx={styles.tb}>{l.description}</TableCell>
                <TableCell sx={styles.tb}>{l.owner?.nickname}</TableCell>
                <TableCell sx={styles.tb}>{numberToPercentFormat(l.interestRate, 2)}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(l.startDate)}</TableCell>
                <TableCell sx={styles.tb}>{dateToLocalFormat(l.endDate)}</TableCell>
                <TableCell sx={styles.tb} align="right">{numberToMoneyFormat(snapshot.getSnapshotBalance(l.id, "Liability"), 0)}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell sx={styles.total}>Total</TableCell>
              <TableCell sx={styles.totalNeg} colSpan={5} align="right">{numberToMoneyFormat(networthCalc.liabilitiesTotal, 0)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <TableContainer component={Paper} sx={{mt:2, bgcolor:grey["200"]}}>
        <Table size="small" width="100%">
          <TableBody>
            <TableCell><Typography variant="h4" color="primary">Net Worth</Typography></TableCell>
            <TableCell sx={styles.totalPos} align="right">{renderNetWorth()}</TableCell>
          </TableBody>
        </Table>
      </TableContainer>

      {networthCalc.excludedAssetsTotal > 0 &&
        <Typography variant="caption" sx={{display:"block", mt:1, fontStyle:"italic", color:"text.secondary"}}>
          *Net Worth shown above excludes {numberToMoneyFormat(networthCalc.excludedAssetsTotal, 0)} in
          assets that are tracked for planning purposes but not included in the household net worth total
          (e.g., 529 plans, donor-advised funds, custodial accounts).
        </Typography>
      }

    </Box>
  )
}

export default NetWorthReport